import React, { useState } from 'react';
import { PageNav, PageFooter, CookieBanner } from '../components/layout/LegalLayout';
import { Mail, MessageSquare, Zap, Check, Loader } from 'lucide-react';

const C = {
  amber100:'#FFF4E0', amber200:'#FFD88A', amber300:'#F5A623', amber400:'#C4811A',
  ink:'#1A1814', inkLight:'#3D3A34', muted:'#7A7570',
  border:'#E8E4DC', surface:'#FAFAF8', white:'#FFFFFF', green:'#16A34A',
};
const font = { head:"'Sora',sans-serif", body:"'DM Sans',sans-serif" };

const ContactPage: React.FC = () => {
  const [form, setForm]     = useState({ name:'', email:'', type:'general', message:'' });
  const [loading, setLoad]  = useState(false);
  const [done, setDone]     = useState(false);
  const [errors, setErrors] = useState<Record<string,string>>({});

  const set = (k: string, v: string) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs: Record<string,string> = {};
    if (!form.name.trim())  errs.name    = 'Required';
    if (!form.email.trim()) errs.email   = 'Required';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Invalid email';
    if (!form.message.trim()) errs.message = 'Required';
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setLoad(true);
    await new Promise(r => setTimeout(r, 1200));
    setLoad(false); setDone(true);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Sora:wght@700;800&family=DM+Sans:opsz,wght@9..40,400;9..40,600;9..40,700&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'DM Sans', sans-serif; background: #FAFAF8; color: #1A1814; }
        #root { display: flex; flex-direction: column; min-height: 100vh; }
        @keyframes spin { to { transform: rotate(360deg); } }

        .contact-layout {
          display: grid;
          grid-template-columns: 300px 1fr;
          gap: 40px;
          align-items: start;
        }
        .contact-name-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }
        .contact-input {
          height: 46px;
          padding: 0 14px;
          border-radius: 10px;
          border: 1.5px solid #E8E4DC;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          color: #1A1814;
          outline: none;
          background: #fff;
          width: 100%;
          transition: border-color .15s;
        }
        .contact-input:focus { border-color: #F5A623; box-shadow: 0 0 0 3px #F5A62322; }
        .contact-input.err   { border-color: #DC2626; }
        .contact-select {
          height: 46px;
          padding: 0 14px;
          border-radius: 10px;
          border: 1.5px solid #E8E4DC;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          color: #1A1814;
          outline: none;
          background: #fff;
          width: 100%;
          cursor: pointer;
        }
        .contact-textarea {
          padding: 12px 14px;
          border-radius: 10px;
          border: 1.5px solid #E8E4DC;
          font-family: 'DM Sans', sans-serif;
          font-size: 15px;
          color: #1A1814;
          outline: none;
          resize: vertical;
          background: #fff;
          width: 100%;
          transition: border-color .15s;
        }
        .contact-textarea:focus { border-color: #F5A623; box-shadow: 0 0 0 3px #F5A62322; }
        .contact-textarea.err   { border-color: #DC2626; }
        .contact-card {
          display: flex;
          gap: 14px;
          padding: 18px 20px;
          margin-bottom: 12px;
          border-radius: 14px;
          border: 1px solid #E8E4DC;
          background: #fff;
          text-decoration: none;
          transition: border-color .15s, box-shadow .15s;
        }
        .contact-card:hover {
          border-color: #F5A623;
          box-shadow: 0 4px 16px #F5A62322;
        }
        .contact-submit {
          height: 50px;
          border-radius: 12px;
          border: none;
          cursor: pointer;
          background: linear-gradient(135deg, #F5A623, #C4811A);
          color: #fff;
          font-weight: 700;
          font-size: 15px;
          font-family: 'DM Sans', sans-serif;
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          box-shadow: 0 6px 20px #F5A62344;
        }
        .contact-submit:disabled { opacity: 0.75; cursor: not-allowed; }

        @media (max-width: 720px) {
          .contact-layout    { grid-template-columns: 1fr; gap: 24px; }
          .contact-name-row  { grid-template-columns: 1fr; }
        }
      `}</style>

      <PageNav/>
      <main style={{ flex:1, maxWidth:1000, margin:'0 auto', width:'100%', padding:'52px clamp(16px,5vw,40px) 80px' }}>

        {/* Header */}
        <div style={{ textAlign:'center', marginBottom:48 }}>
          <h1 style={{ fontFamily:font.head, fontWeight:800, fontSize:'clamp(26px,5vw,44px)', letterSpacing:'-0.04em', color:C.ink, marginBottom:10 }}>
            Get in touch
          </h1>
          <p style={{ fontSize:'clamp(14px,2vw,17px)', color:C.muted, fontFamily:font.body }}>
            We read every message and reply within one business day.
          </p>
        </div>

        <div className="contact-layout">

          {/* Left — contact options */}
          <div>
            {[
              { icon:<Mail size={18}/>,          title:'General',         desc:'Questions or feedback.',     email:'hello@Jötr.co' },
              { icon:<MessageSquare size={18}/>,  title:'Feature requests', desc:'Tell us what Jötr needs.', email:'feedback@Jötr.co' },
              { icon:<Zap size={18}/>,            title:'Bug reports',      desc:'Found something broken?',   email:'bugs@Jötr.co' },
            ].map(opt => (
              <a key={opt.title} className="contact-card" href={`mailto:${opt.email}`}>
                <div style={{ width:40, height:40, borderRadius:11, background:C.amber100, display:'flex', alignItems:'center', justifyContent:'center', color:C.amber400, flexShrink:0 }}>
                  {opt.icon}
                </div>
                <div>
                  <p style={{ fontSize:14, fontWeight:700, color:C.ink, margin:'0 0 2px', fontFamily:font.body }}>{opt.title}</p>
                  <p style={{ fontSize:12, color:C.muted, margin:'0 0 4px', fontFamily:font.body }}>{opt.desc}</p>
                  <p style={{ fontSize:13, color:C.amber400, fontWeight:600, margin:0, fontFamily:font.body }}>{opt.email}</p>
                </div>
              </a>
            ))}

            <div style={{ padding:'16px 18px', borderRadius:12, border:`1px solid ${C.border}`, background:C.surface, marginTop:4 }}>
              <p style={{ fontSize:13, fontWeight:600, color:C.ink, margin:'0 0 4px', fontFamily:font.body }}>Response time</p>
              <p style={{ fontSize:13, color:C.muted, margin:0, fontFamily:font.body, lineHeight:1.6 }}>
                Mon – Fri, 9am – 6pm WAT.<br/>We reply within 1 business day.
              </p>
            </div>
          </div>

          {/* Right — form */}
          <div style={{ background:C.white, borderRadius:20, border:`1px solid ${C.border}`, padding:'clamp(20px,4vw,36px)' }}>
            {done ? (
              <div style={{ textAlign:'center', padding:'32px 0' }}>
                <div style={{ width:60, height:60, borderRadius:'50%', background:'#F0FDF4', border:`2px solid ${C.green}44`, display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 20px' }}>
                  <Check size={26} color={C.green}/>
                </div>
                <h2 style={{ fontFamily:font.head, fontWeight:800, fontSize:22, color:C.ink, marginBottom:10 }}>Message sent!</h2>
                <p style={{ fontSize:15, color:C.muted, fontFamily:font.body, lineHeight:1.7 }}>
                  Thanks for reaching out. We'll reply to{' '}
                  <strong style={{ color:C.ink }}>{form.email}</strong> within one business day.
                </p>
              </div>
            ) : (
              <form onSubmit={submit} noValidate style={{ display:'flex', flexDirection:'column', gap:18 }}>
                <h2 style={{ fontFamily:font.head, fontWeight:700, fontSize:20, color:C.ink, margin:'0 0 4px' }}>Send a message</h2>

                <div className="contact-name-row">
                  {/* Name */}
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <label style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>Your name</label>
                    <input className={`contact-input${errors.name?' err':''}`} type="text" value={form.name} onChange={e=>set('name',e.target.value)} placeholder="Ilori Emmanuel"/>
                    {errors.name && <span style={{ fontSize:12, color:'#DC2626', fontFamily:font.body }}>{errors.name}</span>}
                  </div>
                  {/* Email */}
                  <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                    <label style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>Email address</label>
                    <input className={`contact-input${errors.email?' err':''}`} type="email" value={form.email} onChange={e=>set('email',e.target.value)} placeholder="you@example.com"/>
                    {errors.email && <span style={{ fontSize:12, color:'#DC2626', fontFamily:font.body }}>{errors.email}</span>}
                  </div>
                </div>

                {/* Topic */}
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <label style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>Topic</label>
                  <select className="contact-select" value={form.type} onChange={e=>set('type',e.target.value)}>
                    <option value="general">General question</option>
                    <option value="feature">Feature request</option>
                    <option value="bug">Bug report</option>
                    <option value="billing">Billing</option>
                    <option value="privacy">Privacy / data</option>
                    <option value="partnership">Partnership</option>
                  </select>
                </div>

                {/* Message */}
                <div style={{ display:'flex', flexDirection:'column', gap:6 }}>
                  <label style={{ fontSize:13, fontWeight:600, color:C.inkLight, fontFamily:font.body }}>Message</label>
                  <textarea className={`contact-textarea${errors.message?' err':''}`} value={form.message} onChange={e=>set('message',e.target.value)} placeholder="Describe your question or feedback…" rows={5}/>
                  {errors.message && <span style={{ fontSize:12, color:'#DC2626', fontFamily:font.body }}>{errors.message}</span>}
                </div>

                <button type="submit" disabled={loading} className="contact-submit">
                  {loading
                    ? <><Loader size={16} style={{ animation:'spin .7s linear infinite' }}/> Sending…</>
                    : 'Send message'}
                </button>
              </form>
            )}
          </div>
        </div>
      </main>
      <PageFooter/>
      <CookieBanner/>
    </>
  );
};

export default ContactPage;